import { Fragment, useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./auth.js";

export type NavIcon =
  | "dashboard"
  | "newBooking"
  | "bookings"
  | "calls"
  | "callbacks"
  | "jobs"
  | "customers"
  | "services"
  | "rules"
  | "invoices"
  | "settings"
  | "security"
  | "clients"
  | "licences"
  | "rates"
  | "agent"
  | "numbers"
  | "usage"
  | "debug"
  | "billing"
  | "audit";

export interface NavItem {
  to: string;
  label: string;
  icon?: NavIcon;
  /** Draws a thin separator above this item, to group the list without adding labels. */
  separated?: boolean;
}

/** 24x24 line icons (1.75 stroke, round caps), drawn for currentColor so the shell can tint them. */
const ICONS: Record<NavIcon, ReactNode> = {
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </>
  ),
  newBooking: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4M12 12.5v5M9.5 15h5" />
    </>
  ),
  bookings: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  calls: <path d="M5 4h3.5l1.8 4.6-2.2 1.4a11 11 0 0 0 5.9 5.9l1.4-2.2L20 15.5V19a1.5 1.5 0 0 1-1.6 1.5C10.6 20 4 13.4 3.5 5.6A1.5 1.5 0 0 1 5 4Z" />,
  jobs: (
    <>
      <rect x="3.5" y="8" width="17" height="12" rx="2.5" />
      <path d="M9 8V6a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 6v2M3.5 13.5h17" />
    </>
  ),
  callbacks: (
    <>
      <path d="M5 4h3.5l1.8 4.6-2.2 1.4a11 11 0 0 0 5.9 5.9l1.4-2.2L20 15.5V19a1.5 1.5 0 0 1-1.6 1.5C10.6 20 4 13.4 3.5 5.6A1.5 1.5 0 0 1 5 4Z" />
      <path d="M14.5 3.5v4h4M14.5 7.5l5-5" />
    </>
  ),
  customers: (
    <>
      <circle cx="9" cy="8.5" r="3.5" />
      <path d="M2.5 20c.4-3.6 3-5.5 6.5-5.5s6.1 1.9 6.5 5.5" />
      <path d="M16 5.2a3.4 3.4 0 0 1 0 6.6M18.5 14.8c1.6.8 2.8 2.4 3 5.2" />
    </>
  ),
  services: (
    <>
      <path d="m12 3 9 4.5-9 4.5-9-4.5L12 3Z" />
      <path d="m3 12 9 4.5 9-4.5M3 16.5 12 21l9-4.5" />
    </>
  ),
  rules: (
    <>
      <path d="M12 3 4.5 6v5.5c0 4.4 3 8 7.5 9.5 4.5-1.5 7.5-5.1 7.5-9.5V6L12 3Z" />
      <path d="m8.8 12 2.2 2.2 4.2-4.4" />
    </>
  ),
  invoices: (
    <>
      <path d="M6 3h8.5L19 7.5V20a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5M8.5 13h7M8.5 17h5" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
      <circle cx="15" cy="7" r="2" />
      <circle cx="9" cy="17" r="2" />
    </>
  ),
  security: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5M12 14.5v2.5" />
    </>
  ),
  clients: (
    <>
      <path d="M4 20.5V5.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v15M14 10h5a1 1 0 0 1 1 1v9.5M2.5 20.5h19" />
      <path d="M8 8.5h2M8 12h2M8 15.5h2" />
    </>
  ),
  licences: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="m11 12 8.5-8.5M16 7l2.5 2.5M14 9l2 2" />
    </>
  ),
  rates: (
    <>
      <path d="M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7a1 1 0 0 1 .7.3l7.6 7.6a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0l-7.9-7.9a1 1 0 0 1-.3-.7Z" />
      <circle cx="8" cy="8" r="1.2" />
    </>
  ),
  agent: (
    <>
      <rect x="4" y="7" width="16" height="12" rx="3" />
      <path d="M12 3.5V7M9 12.5v1.5M15 12.5v1.5M2 12v3M22 12v3" />
    </>
  ),
  numbers: <path d="M9 3.5 7.5 20.5M16.5 3.5 15 20.5M4 9h16.5M3.5 15H20" />,
  usage: <path d="M3 12h4l2.5-7 5 14 2.5-7H21" />,
  debug: (
    <>
      <rect x="7.5" y="8" width="9" height="12" rx="4.5" />
      <path d="M9.5 8a2.5 2.5 0 0 1 5 0M7.5 13H3.5M20.5 13h-4M7.8 17.5l-3 1.5M16.2 17.5l3 1.5M7.8 9l-3-1.5M16.2 9l3-1.5M12 12v8" />
    </>
  ),
  billing: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <path d="M3 10h18M7 15h3" />
    </>
  ),
  audit: (
    <>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.5 8.5" />
      <path d="M3.5 4v4.5H8M12 7.5V12l3 2" />
    </>
  ),
};

function Icon({ name }: { name: NavIcon }) {
  return (
    <svg className="rc-shell-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {ICONS[name]}
    </svg>
  );
}

function Brand({ title, subtitle, hint }: { title: string; subtitle: string; hint?: string }) {
  return (
    <div className="rc-shell-brand">
      <div className="rc-shell-brandtext">
        <div className="rc-shell-title">{title}</div>
        <div className="rc-shell-sub" title={hint}>{subtitle}</div>
      </div>
    </div>
  );
}

/**
 * Shared shell layout for both /app and /admin (docs/architecture.md section 10): a sidebar on
 * wide screens, and below 900px a top bar with the nav as a slide-out drawer (styles/responsive.css).
 */
export function Shell({ title, subtitle = "Receptionist", items, banner, note }: { title: string; subtitle?: string; items: NavItem[]; banner?: ReactNode; note?: ReactNode }) {
  const { signOut, session } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the drawer after navigating, and on Escape.
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="rc-shell">
      <header className="rc-shell-topbar">
        <button className="rc-shell-menu" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <Brand title={title} subtitle={subtitle} hint={session?.user.email ?? undefined} />
      </header>
      <div className={`rc-shell-backdrop${open ? " open" : ""}`} onClick={() => setOpen(false)} />
      <nav className={`rc-shell-nav${open ? " open" : ""}`} aria-label="Main">
        <Brand title={title} subtitle={subtitle} hint={session?.user.email ?? undefined} />
        <div className="rc-shell-links">
          {items.map((item) => (
            <Fragment key={item.to}>
              {item.separated && <div className="rc-shell-sep" role="separator" />}
              <NavLink to={item.to} end={item.to === "/app" || item.to === "/admin"} className={({ isActive }) => `rc-shell-link${isActive ? " active" : ""}`}>
                {item.icon && <Icon name={item.icon} />}
                {item.label}
              </NavLink>
            </Fragment>
          ))}
          {note && <div className="rc-shell-note">{note}</div>}
        </div>
        <div className="rc-shell-foot">
          <button className="rc-shell-signout" onClick={() => void signOut()}>
            <svg className="rc-shell-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M9 4.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 19.5h3M15 8l4 4-4 4M19 12H9.5" />
            </svg>
            Sign out
          </button>
        </div>
      </nav>
      <main className="rc-shell-main">
        {banner}
        <Outlet />
      </main>
    </div>
  );
}

export function Placeholder({ title }: { title: string }) {
  return (
    <div>
      <h1 className="rc-h1">{title}</h1>
      <p style={{ color: "var(--rc-text-faint)" }}>Not built yet.</p>
    </div>
  );
}
